import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ChevronLeft, FileText, Package, Printer, RotateCcw, Truck } from "lucide-react";
import { api, fmtEur, getErrorMessage } from "../../api";
import { downloadAuthedFile } from "../../utils/download";
import { useCart } from "../../CartContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);

  if (!orders) return <div className="max-w-4xl mx-auto p-6 text-[#5C665A]" data-testid="orders-loading">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <div className="db-h mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="orders-title">My orders</h1>
        <p className="text-sm text-[#5C665A]">Submitted requests. We invoice separately once your order is confirmed.</p>
      </div>
      {orders.length === 0 ? (
        <div className="bg-white border border-[#E2E4E0] rounded-lg p-10 text-center" data-testid="orders-empty">
          <Package size={28} className="text-[#5C665A] mx-auto mb-2" />
          <p className="text-sm text-[#5C665A]">You haven&apos;t submitted any orders yet.</p>
          <Link to="/catalog" className="text-sm text-[#2D6A4F] hover:underline">Browse the catalog →</Link>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="bg-[#F9F9F8] text-[#5C665A]">
              <tr>
                <th className="text-left px-3 py-2 db-h font-semibold">Order</th>
                <th className="text-left px-3 py-2 db-h font-semibold">Date</th>
                <th className="text-center px-3 py-2 db-h font-semibold">Status</th>
                <th className="text-right px-3 py-2 db-h font-semibold">Items</th>
                <th className="text-right px-3 py-2 db-h font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-[#E2E4E0] db-row" data-testid={`orders-row-${o.id}`}>
                  <td className="px-3 py-2"><Link to={`/orders/${o.id}`} className="db-mono font-medium hover:underline" data-testid={`orders-number-${o.id}`}>{o.order_number}</Link></td>
                  <td className="px-3 py-2 text-[#5C665A]">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-center"><StatusBadge status={o.status} /></td>
                  <td className="px-3 py-2 text-right db-mono">{o.lines.reduce((s, l) => s + l.quantity, 0)}</td>
                  <td className="px-3 py-2 text-right db-mono font-semibold">{fmtEur(o.total || o.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-amber-50", text: "text-amber-800", label: "Pending review" },
    confirmed: { bg: "bg-[#E9F5EC]", text: "text-[#1B4332]", label: "Confirmed" },
    packed: { bg: "bg-blue-50", text: "text-blue-800", label: "Packed" },
    shipped: { bg: "bg-[#1A1D1A]", text: "text-white", label: "Shipped" },
    cancelled: { bg: "bg-neutral-100", text: "text-neutral-500", label: "Cancelled" },
  };
  const s = map[status] || map.pending;
  return <Badge className={`${s.bg} ${s.text} border-transparent`}>{s.label}</Badge>;
}

export function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reload: reloadCart } = useCart();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => setO({ notFound: true })); }, [id]);
  if (!o) return <div className="max-w-3xl mx-auto p-6 text-[#5C665A]">Loading…</div>;
  if (o.notFound) return <div className="max-w-3xl mx-auto p-6 text-[#5C665A]">Order not found.</div>;

  const reorder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${o.id}/reorder`);
      const changes = data.changes || [];
      await reloadCart();  // refresh cart badge immediately
      if (changes.length) toast.warning(`Adjusted ${changes.length} line${changes.length > 1 ? "s" : ""}: ${changes.map((c) => c.line + " (" + c.action + ")").join(", ")}`);
      else toast.success("Cart refilled");
      navigate("/cart");
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid={`order-detail-${o.id}`}>
      <Link to="/orders" className="text-sm text-[#5C665A] hover:text-[#1A1D1A] inline-flex items-center gap-1 mb-3" data-testid="order-back">
        <ChevronLeft size={14} /> All orders
      </Link>
      <div className="bg-white border border-[#E2E4E0] rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="db-h">
            <h1 className="text-xl font-bold tracking-tight" data-testid="order-number">{o.order_number}</h1>
            <div className="text-xs text-[#5C665A] mt-0.5">Submitted {new Date(o.created_at).toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={o.status} />
            {o.payment_status === "paid" && (
              <span className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-[#1B4332] text-white text-sm font-medium" data-testid="order-paid-badge">
                <CheckCircle2 size={14} /> PAID
              </span>
            )}
            {o.payment_status !== "paid" && o.stripe_payment_url && (
              <a href={o.stripe_payment_url} target="_blank" rel="noreferrer" data-testid="order-pay-now">
                <Button className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white">
                  Pay now
                </Button>
              </a>
            )}
            {o.invoice_number && (
              <Button
                variant="outline"
                className="h-9"
                data-testid="order-invoice-download"
                onClick={() => downloadAuthedFile(
                  `/api/orders/${o.id}/invoice.pdf`,
                  `${o.invoice_number}.pdf`,
                  () => toast.error("Invoice download failed"),
                )}
              >
                <FileText size={14} /> Invoice
              </Button>
            )}
            <a href={`/orders/${o.id}/print`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="h-9" data-testid="order-print"><Printer size={14} /> PDF</Button>
            </a>
            <Button disabled={busy} onClick={reorder} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white" data-testid="order-reorder">
              <RotateCcw size={14} /> Reorder
            </Button>
          </div>
        </div>

        <Timeline status={o.status} history={o.status_history} />

        {o.status === "pending" && (
          <div className="mt-3 bg-[#E9F5EC] text-[#1B4332] rounded-md px-3 py-2 text-sm inline-flex items-start gap-2" data-testid="order-pending-note">
            <CheckCircle2 size={14} className="mt-0.5" />
            Your request has been received. We&apos;ll review it and send you a formal quote.
          </div>
        )}

        {o.tracking_number && (o.status === "shipped" || o.status === "packed") && (
          <div className="mt-3 bg-[#E9F5EC] border border-[#BFDBC5] rounded-md px-3 py-2 text-sm inline-flex items-center gap-2" data-testid="order-tracking">
            <Truck size={14} className="text-[#1B4332]" />
            <span className="text-[#5C665A]">Tracking</span>
            {o.tracking_url ? (
              <a href={o.tracking_url} target="_blank" rel="noreferrer" className="db-mono text-[#1B4332] underline underline-offset-2 hover:text-[#0F2E1F]">{o.tracking_number}</a>
            ) : (
              <span className="db-mono text-[#1B4332]">{o.tracking_number}</span>
            )}
            {o.tracking_carrier && <span className="text-[#5C665A]">· {o.tracking_carrier}</span>}
          </div>
        )}

        <div className="mt-5 border-y border-[#E2E4E0] divide-y divide-[#E2E4E0]">
          {o.lines.map((l) => (
            <div key={l.id} className="py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1" data-testid={`order-line-${l.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium break-words">{l.product_name} <span className="text-[#5C665A] font-normal">· {l.size_name}{l.size_note ? ` (${l.size_note})` : ""}</span></div>
                <div className="text-xs db-mono text-[#5C665A]">{l.sku}</div>
              </div>
              <div className="text-right sm:text-right shrink-0">
                <div className="db-mono text-sm text-[#5C665A]">{l.quantity} × {fmtEur(l.unit_price)}</div>
                <div className="db-mono font-semibold">{fmtEur(l.line_total)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 space-y-1 text-sm max-w-xs ml-auto">
          <div className="flex justify-between"><span className="text-[#5C665A]">Subtotal</span><span className="db-mono" data-testid="order-subtotal">{fmtEur(o.subtotal)}</span></div>
          {o.discount_amount > 0 && (
            <div className="flex justify-between text-[#C94A4A]"><span>Discount</span><span className="db-mono" data-testid="order-discount">− {fmtEur(o.discount_amount)}</span></div>
          )}
          {(o.shipping_cost > 0 || ["confirmed", "packed", "shipped"].includes(o.status)) && (
            <div className="flex justify-between"><span className="text-[#5C665A]">Shipping</span><span className="db-mono" data-testid="order-shipping">{fmtEur(o.shipping_cost || 0)}</span></div>
          )}
          {o.phyto_cost > 0 && (
            <div className="flex justify-between"><span className="text-[#5C665A]">Phytosanitary</span><span className="db-mono" data-testid="order-phyto">{fmtEur(o.phyto_cost)}</span></div>
          )}
          <div className="flex justify-between border-t border-[#E2E4E0] pt-1"><span className="db-h font-semibold">Total (EUR, VAT-excl)</span><span className="db-mono text-lg font-bold" data-testid="order-total">{fmtEur(o.total || o.subtotal)}</span></div>
        </div>

        {/* Bottom Pay online now — mirrors top button when confirmed & unpaid. */}
        {o.payment_status !== "paid" && o.stripe_payment_url && o.status !== "cancelled" && (
          <div className="mt-4 flex justify-end">
            <a href={o.stripe_payment_url} target="_blank" rel="noreferrer" data-testid="order-pay-now-bottom" className="inline-block w-full sm:w-auto">
              <Button className="h-11 px-6 bg-[#2D6A4F] hover:bg-[#1B4332] text-white text-base font-semibold w-full sm:w-auto">
                Pay online now — {fmtEur(o.total || o.subtotal)}
              </Button>
            </a>
          </div>
        )}
        {o.payment_status === "paid" && (
          <div className="mt-4 flex justify-end items-center gap-2" data-testid="order-paid-block">
            <span className="inline-flex items-center gap-1 h-11 px-4 rounded-md bg-[#1B4332] text-white text-sm font-semibold">
              <CheckCircle2 size={16} /> PAID
              {o.paid_at && <span className="opacity-80 text-xs font-normal ml-1">· {new Date(o.paid_at).toLocaleString()}</span>}
            </span>
          </div>
        )}

        {o.note && (
          <div className="mt-3 text-xs text-[#5C665A]"><span className="font-medium">Note:</span> {o.note}</div>
        )}
      </div>
    </div>
  );
}

function Timeline({ status, history, paidAt }) {
  const steps = ["pending", "confirmed", "packed", "shipped"];
  const activeIdx = status === "cancelled" ? -1 : steps.indexOf(status);
  if (status === "cancelled") {
    return (
      <div className="mt-4 flex items-center gap-2 text-sm text-[#5C665A]" data-testid="order-timeline">
        <Truck size={14} /> Order cancelled.
      </div>
    );
  }
  // Look up transition timestamps from status_history for tooltips.
  const stampFor = (s) => {
    const h = (history || []).find((r) => r.status === s);
    return h ? new Date(h.at).toLocaleString() : null;
  };
  return (
    <ol className="mt-4 flex flex-wrap items-center gap-1.5 text-xs" data-testid="order-timeline">
      {steps.map((s, i) => {
        const stamp = stampFor(s);
        return (
        <li key={s} className="flex items-center gap-1.5" data-testid={`order-timeline-step-${s}`} title={stamp || ""}>
          <span className={`w-2.5 h-2.5 rounded-full ${i <= activeIdx ? "bg-[#2D6A4F]" : "bg-neutral-200"}`} />
          <span className={i === activeIdx ? "font-medium text-[#1A1D1A]" : "text-[#5C665A]"}>{s}</span>
          {i < steps.length - 1 && <span className="w-4 h-px bg-[#E2E4E0]" />}
        </li>
        );
      })}
      {paidAt && (
        <li className="flex items-center gap-1.5 ml-2 pl-2 border-l border-[#E2E4E0]" data-testid="order-timeline-step-paid" title={new Date(paidAt).toLocaleString()}>
          <span className="w-2.5 h-2.5 rounded-full bg-[#1B4332]" />
          <span className="font-medium text-[#1B4332]">paid</span>
        </li>
      )}
    </ol>
  );
}

/** Buyer print page — clean PDF-ready layout. */
export function OrderPrint() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setO(r.data)); }, [id]);
  if (!o) return <div className="p-6 text-sm text-[#5C665A]">Loading…</div>;
  return (
    <div className="min-h-screen bg-white text-[#1A1D1A]" data-testid="order-print-page">
      <div className="max-w-2xl mx-auto px-8 py-10 print:py-0 print:px-4">
        <div className="flex items-start justify-between border-b border-[#E2E4E0] pb-4">
          <div className="db-h">
            <div className="text-[#2D6A4F] text-base font-bold">DocBlock Wholesale</div>
            <div className="text-2xl font-bold mt-2">Order confirmation</div>
            <div className="text-sm text-[#5C665A] mt-0.5">Order <span className="db-mono">{o.order_number}</span> · {new Date(o.created_at).toLocaleDateString()}</div>
          </div>
          <button onClick={() => window.print()} className="print:hidden inline-flex items-center gap-1 h-9 px-3 rounded-md bg-[#2D6A4F] hover:bg-[#1B4332] text-white text-sm" data-testid="order-print-btn">
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>

        <table className="w-full mt-6 text-sm border-t border-[#E2E4E0]">
          <thead className="text-[#5C665A]"><tr><th className="text-left py-2 db-h font-semibold">Product · Size</th><th className="text-left py-2 db-h font-semibold">SKU</th><th className="text-right py-2 db-h font-semibold">Qty</th><th className="text-right py-2 db-h font-semibold">Unit</th><th className="text-right py-2 db-h font-semibold">Line</th></tr></thead>
          <tbody>
            {o.lines.map((l) => (
              <tr key={l.id} className="border-t border-[#E2E4E0]">
                <td className="py-2">{l.product_name} · {l.size_name}</td>
                <td className="py-2 db-mono text-xs">{l.sku}</td>
                <td className="py-2 text-right db-mono">{l.quantity}</td>
                <td className="py-2 text-right db-mono">{fmtEur(l.unit_price)}</td>
                <td className="py-2 text-right db-mono">{fmtEur(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 ml-auto max-w-xs text-sm">
          <div className="flex justify-between"><span className="text-[#5C665A]">Subtotal</span><span className="db-mono">{fmtEur(o.subtotal)}</span></div>
          {o.discount_amount > 0 && <div className="flex justify-between text-[#C94A4A]"><span>Discount</span><span className="db-mono">− {fmtEur(o.discount_amount)}</span></div>}
          {(o.shipping_cost > 0 || ["confirmed", "packed", "shipped"].includes(o.status)) && <div className="flex justify-between"><span className="text-[#5C665A]">Shipping</span><span className="db-mono">{fmtEur(o.shipping_cost || 0)}</span></div>}
          {o.phyto_cost > 0 && <div className="flex justify-between"><span className="text-[#5C665A]">Phytosanitary</span><span className="db-mono">{fmtEur(o.phyto_cost)}</span></div>}
          <div className="flex justify-between border-t border-[#1A1D1A] mt-1 pt-1"><span className="db-h font-semibold">Total (EUR, VAT-excl)</span><span className="db-mono font-bold">{fmtEur(o.total || o.subtotal)}</span></div>
        </div>
        {o.note && <div className="mt-6 pt-4 border-t border-[#E2E4E0] text-sm"><div className="text-xs uppercase text-[#5C665A] db-h font-semibold">Note</div><div>{o.note}</div></div>}
        <div className="mt-10 text-[10px] text-[#5C665A] print:mt-6">
          All prices EUR, VAT-exclusive. Shipping is quoted after review. No payment is taken now.
        </div>
      </div>
      <style>{`@media print { body { background: white; } .print\\:hidden { display: none !important; } }`}</style>
    </div>
  );
}
